import { createContext } from "react";
import { orgAdminStatsResponseSchema, WebotServerContextProps } from "../@types/webotServer";
import useAuth from "../hooks/useAuth";

import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import { reviveContactDocument } from "hooks/contacts/contactDocumentRules";
import { readWebotErrorBody, WebotServerError } from "utils/contacts/contactWriteErrors";
import { SalesMapError } from "utils/salesMap/salesMapError";
import { SalesTrainingError } from "utils/salesTraining/salesTrainingError";
import { SalesRecruitingError } from "utils/salesRecruiting/salesRecruitingError";
import { forgetListingPresentationForAdd } from "utils/minisite/listingPresentationForAdd";
import { CalendarEvent, CoinBalanceBreakdown, REST_schema } from "../@types/sharedSchema";
import type {
  AddFacebookSourceRequest,
  AddFacebookSourceResponse,
  ImportFacebookSourcesRequest,
  ImportFacebookSourcesResponse,
  CalendarClientNoteStatusResponse,
  CheckFacebookSourceRequest,
  CheckFacebookSourceResponse,
  ConvertFacebookLeadRequest,
  FacebookAdminSourceDetailResponse,
  FacebookLeadStatus,
  FacebookRefreshStatus,
  GetFacebookAdminSourcesStatsResponse,
  GetFacebookLeadDetailResponse,
  GetFacebookLeadNotesResponse,
  GetFacebookLeadsRequest,
  GetFacebookLeadsResponse,
  GetFacebookPersonHistoryRequest,
  GetFacebookPersonHistoryResponse,
  GetFacebookSourcesResponse,
  StartFacebookRefreshResponse,
  UpdateFacebookSourceRequest,
  UserNote
} from "../@types/sharedSchema";
import type {
  ListingCardPage,
  MarkNoSpamContactsResponse,
  StreetNeighborhoodResponse,
  WhatsappConnectDiagnostics
} from "../@types/webotServer";
import type {
  CrmContactsBulkUpdateRequest,
  CrmContactsBulkUpdateResponse,
  CrmContactsDeleteRequest,
  CrmContactsDeleteResponse,
  CrmContactsExportFile,
  CrmContactsExportRequest,
  CrmContactHoldersRequest,
  CrmContactHoldersResponse,
  ContactsLookupResponse,
  ContactsListingSellersResponse,
  ContactsPresenceResponse,
  ContactsSummaryResponse,
  CrmContactsListRequest,
  CrmContactsListResponse,
  CrmContactUpdateRequest,
  CrmContactCreateRequest,
  CrmContactCreateResponse,
  CrmContactsBulkCreateRequest,
  CrmContactsBulkCreateResponse,
  CrmRecordBulkMessageRequest,
  CrmRecordBulkMessageResponse,
  CrmContactUpdateResponse,
  CrmLeadsBoardResponse,
  CrmOrgMemberRoleUpdateRequest,
  CrmOrgMemberUpdateRequest,
  CrmOrgMembersBulkUpdateRequest,
  CrmOrgMembersBulkUpdateResponse,
  CrmOrgMemberView,
  CrmOrgOverviewResponse,
  CrmOrgSettings,
  CrmOrgSettingsUpdateRequest,
  OrgDirectoryResponse
} from "../@types/webotServer";
import type { ReactNode } from "react";

const WebotServerContext = createContext({} as WebotServerContextProps);

type WebotServerProviderProps = {
  children: ReactNode;
};

function WebotServerProvider({ children }: WebotServerProviderProps) {
  const { user } = useAuth();

  const baseUrl = `${WEBOT_SERVER_ENDPOINT}/api/v1`;

  const sendWebotRequest = async (
    url: string,
    method: "POST" | "GET" | "DELETE",
    body: any = {}
  ): Promise<any> => {
    let userIdToken: string | undefined;

    if (user?.id) {
      userIdToken = await user?.getIdToken();
    }

    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (userIdToken) headers.Authorization = userIdToken;

    const response = await fetch(url, {
      method,
      body: method === "POST" ? JSON.stringify(body) : undefined,
      headers
    });

    const text = await response.text();
    const contentType = response.headers.get("content-type") ?? "";

    // The message string is byte-identical to what this used to throw — it is what shows
    // up in Sentry and in a console, and several eyes already know its shape. What is NEW
    // is that the status and the server's own error code survive as READABLE FIELDS
    // instead of only as text inside it, so a caller can tell "the server refused this
    // request" (400 — permanent, our bug, needs specific copy) from "the server fell over"
    // (5xx — retryable) without matching a substring. See `WebotServerError`.
    const buildError = (reason: string): WebotServerError => {
      const preview = text.slice(0, 200).replace(/\s+/g, " ").trim();
      // Every JSON error this server emits is `{ error, message, statusCode }`, and some
      // routes say more beside them — the contact endpoints name the refused `field` — so the
      // body rides the error too (`WebotServerError.details`). One reader for both helpers.
      const { code, serverMessage, details } = readWebotErrorBody(contentType, text);
      return new WebotServerError(
        `Webot ${method} ${url} ${reason} [status=${response.status}] [content-type="${contentType}"] [body="${preview}"]`,
        response.status,
        code,
        serverMessage,
        details
      );
    };

    if (!response.ok) {
      throw buildError("failed");
    }

    if (!text) return undefined;

    if (!contentType.toLowerCase().includes("application/json")) {
      throw buildError("returned non-JSON");
    }

    try {
      return JSON.parse(text);
    } catch {
      throw buildError("JSON parse failed");
    }
  };

  const getOrgAdminStats = async (dateFrom: Date, dateTo: Date, organizationId?: string) => {
    const url = `${baseUrl}/get-data/org-admin-stats`;
    return (await sendWebotRequest(url, "POST", {
      organizationId: organizationId,
      ...(dateFrom && { dateFrom: dateFrom }),
      ...(dateTo && { dateTo: dateTo })
    })) as orgAdminStatsResponseSchema;
  };

  const markNoSpamContacts = async (): Promise<MarkNoSpamContactsResponse> => {
    const url = `${baseUrl}/contacts/mark-no-spam`;
    return await sendWebotRequest(url, "POST");
  };

  const getBuyersSuggestionsForListingAndRealtor = async (
    listingId: string
  ): Promise<REST_schema.RelevantContactsForListingResponse> => {
    const url = `${baseUrl}/getBuyersSuggestionsForListingAndRealtor/${listingId}`;
    return (await sendWebotRequest(url, "POST")) as REST_schema.RelevantContactsForListingResponse;
  };

  /** The LAST computed matches for (caller, listing) — one read, while the live compute runs. */
  const getLastBuyersSuggestionsForListing = async (
    listingId: string
  ): Promise<REST_schema.LastRelevantContactsForListingResponse> => {
    const url = `${baseUrl}/getBuyersSuggestionsForListingAndRealtor/${listingId}/last`;
    return (await sendWebotRequest(url, "POST")) as REST_schema.LastRelevantContactsForListingResponse;
  };

  const getListingCardPage = async (cardId: string): Promise<ListingCardPage> =>
    (await sendWebotRequest(`${baseUrl}/cards/page/${encodeURIComponent(cardId)}`, "GET")) as ListingCardPage;

  const getListingOrgActivity = async (
    listingIds: string[]
  ): Promise<REST_schema.ListingOrgActivityResponse> => {
    const url = `${baseUrl}/listing-org-activity/query`;
    return (await sendWebotRequest(url, "POST", {
      listingIds
    })) as REST_schema.ListingOrgActivityResponse;
  };

  // --- Listing claim / marketplace-source links ---------------------------
  //
  // A realtor takes her own copy of a market ad so she can correct it and record
  // its seller; the copy keeps a link back to every marketplace ad for the same
  // property, and her board then shows the copy instead of the original.

  /**
   * One-click claim: creates the copy server-side from the market ad.
   *
   * Idempotent per (caller, sourceListingId) — a double-tap returns the existing
   * copy with `alreadyClaimed: true` rather than minting a second one.
   *
   * Use this for the quick path. When the realtor wants to review and correct
   * before saving, let `NewListingDialog` create the copy and call
   * `linkListingSource` with the id `onListingCreation` hands back instead.
   */
  const claimMarketListing = async (
    sourceListingId: string,
    seller?: REST_schema.ListingSellerInput
  ): Promise<REST_schema.ClaimListingResponse> =>
    await sendWebotRequest(`${baseUrl}/listings/claim`, "POST", { sourceListingId, seller });

  /** Attach a marketplace ad to a listing the caller owns. */
  const linkListingSource = async (
    listingId: string,
    sourceListingId: string
  ): Promise<REST_schema.LinkListingSourceResponse> =>
    await sendWebotRequest(`${baseUrl}/listings/link-source`, "POST", { listingId, sourceListingId });

  /** Detach one — the market original returns to her board on the next search. */
  const unlinkListingSource = async (
    listingId: string,
    sourceListingId: string
  ): Promise<REST_schema.LinkListingSourceResponse> =>
    await sendWebotRequest(`${baseUrl}/listings/unlink-source`, "POST", { listingId, sourceListingId });

  /**
   * Stamp the CRM contact behind the listing's seller entry. Pass `null` to
   * unlink — the typed name and phone stay, only the link is cleared.
   */
  const linkListingContact = async (
    listingId: string,
    contactId: string | null,
    contactIndex?: number
  ): Promise<{ listingId: string }> =>
    await sendWebotRequest(`${baseUrl}/listings/link-contact`, "POST", {
      listingId,
      contactId,
      contactIndex
    });

  /** The ranked "published versions" dropdown. Ranks; never links anything itself. */
  const getListingSourceCandidates = async (
    request: REST_schema.ListingSourceCandidatesRequest
  ): Promise<REST_schema.ListingSourceCandidatesResponse> =>
    await sendWebotRequest(`${baseUrl}/listings/source-candidates`, "POST", request);

  const webotCalendarOnEventCreated = async (event: CalendarEvent): Promise<void> => {
    const url = `${baseUrl}/webot-calendar/create-event`;
    await sendWebotRequest(url, "POST", { event });
  };

  const webotCalendarOnEventUpdated = async (
    event: CalendarEvent,
    timesChanged: boolean
  ): Promise<void> => {
    const url = `${baseUrl}/webot-calendar/update-event`;
    // `timesChanged` tells the server whether to WhatsApp the client: only a
    // real date/time change should notify — a colour/title/description edit
    // syncs to Google but must not re-message anyone.
    await sendWebotRequest(url, "POST", { event, timesChanged });
  };

  const webotCalendarOnEventDeleted = async (event: CalendarEvent): Promise<void> => {
    const url = `${baseUrl}/webot-calendar/delete-event`;
    await sendWebotRequest(url, "POST", { event });
  };

  /**
   * Office tasks: put a task in a colleague's calendar, or hand an existing one over. The
   * server checks that the two of you share an office, writes the task into THEIR calendar and
   * WhatsApps them. A refusal is a `WebotServerError` whose `code` names it
   * (`not_a_colleague`, `not_your_task`, `task_not_found`, `invalid_task`).
   */
  const calendarTaskAssign = async (
    request: REST_schema.CalendarTaskAssignRequest
  ): Promise<REST_schema.CalendarTaskAssignResponse> =>
    (await sendWebotRequest(`${baseUrl}/calendar-tasks/assign`, "POST", request)) as REST_schema.CalendarTaskAssignResponse;

  /** The colleague who assigned a task edits it — it lives in the other person's calendar. */
  const calendarTaskUpdate = async (
    request: REST_schema.CalendarTaskUpdateRequest
  ): Promise<REST_schema.CalendarTaskUpdateResponse> =>
    (await sendWebotRequest(`${baseUrl}/calendar-tasks/update`, "POST", request)) as REST_schema.CalendarTaskUpdateResponse;

  /** The colleague who assigned a task removes it from the other person's calendar. */
  const calendarTaskCancel = async (request: REST_schema.CalendarTaskCancelRequest): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/calendar-tasks/cancel`, "POST", request);
  };

  /**
   * Whether the calendar form may OFFER the "message to invitees" input.
   *
   * ⚠️ CALL `useCalendarClientNoteEnabled()` INSTEAD of this, from UI code. A failure here
   * (network, 5xx, a server that doesn't have the endpoint yet) THROWS, and a throw is not
   * "off": the hook keeps the two apart, caches the answer and retries a failed lookup.
   */
  const getCalendarClientNoteStatus = async (): Promise<CalendarClientNoteStatusResponse> => {
    const url = `${baseUrl}/webot-calendar/client-note-status`;
    return (await sendWebotRequest(url, "GET")) as CalendarClientNoteStatusResponse;
  };

  // --- Google Calendar two-way sync ---------------------------------------

  const googleCalendarGetStatus = async (): Promise<REST_schema.GoogleCalendarStatusResponse> =>
    await sendWebotRequest(`${baseUrl}/google-calendar/status`, "GET");

  /**
   * Returns Google's consent URL. The caller opens it in a popup and waits for
   * the `webot-google-calendar` postMessage the callback page sends back.
   */
  const googleCalendarStartConnect = async (
    returnPath?: string
  ): Promise<REST_schema.GoogleCalendarConnectResponse> =>
    await sendWebotRequest(`${baseUrl}/google-calendar/connect`, "POST", { returnPath });

  const googleCalendarDisconnect = async (): Promise<REST_schema.GoogleCalendarStatusResponse> =>
    await sendWebotRequest(`${baseUrl}/google-calendar/disconnect`, "POST");

  const googleCalendarSyncNow = async (): Promise<REST_schema.GoogleCalendarStatusResponse> =>
    await sendWebotRequest(`${baseUrl}/google-calendar/sync-now`, "POST");

  const googleCalendarListCalendars = async (): Promise<REST_schema.GoogleCalendarListResponse> =>
    await sendWebotRequest(`${baseUrl}/google-calendar/calendars`, "GET");

  const googleCalendarUpdateSettings = async (
    updates: REST_schema.GoogleCalendarUpdateSettingsRequest
  ): Promise<REST_schema.GoogleCalendarStatusResponse> =>
    await sendWebotRequest(`${baseUrl}/google-calendar/settings`, "POST", updates);

  const getWebotRealtorInfo = async (realtorId: string) => {
    // Guard centrally: never POST without a mail (the backend 400s on missing realtorMail).
    if (!realtorId) return null;
    const url = `${baseUrl}/public/realtor-info`;
    return await sendWebotRequest(url, "POST", { realtorMail: realtorId });
  };

  const publishListingToMadlan = async (
    processedListingId: string,
    confirmedAddress?: string,
    locationResolution?: REST_schema.MadlanLocationResolutionType
  ): Promise<REST_schema.MadlanPublishListingResponse> => {
    const url = `${baseUrl}/madlan/publish-listing`;
    const requestBody: REST_schema.MadlanPublishListingRequest = {
      processedListingId,
      ...(confirmedAddress && { confirmedAddress }),
      ...(locationResolution && { locationResolution })
    };
    return (await sendWebotRequest(
      url,
      "POST",
      requestBody
    )) as REST_schema.MadlanPublishListingResponse;
  };

  const madlanAutocomplete = async (
    request: REST_schema.MadlanAutocompleteRequest
  ): Promise<REST_schema.MadlanAutocompleteResponse> => {
    const url = `${baseUrl}/madlan/autocomplete`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.MadlanAutocompleteResponse;
  };

  const contactSearch = async (searchTerm: string) => {
    const url = `${baseUrl}/contacts/search`;
    return await sendWebotRequest(url, "POST", { searchTerm });
  };

  /**
   * Server-side contacts list, backed by the Postgres mirror.
   *
   * Everything — filtering, sorting, Hebrew full-text search, paging and the facet
   * counts — happens on the server. The browser never holds the whole book; one
   * real user has 25,066 contacts.
   *
   * Scope is decided server-side from `dashboard-orgs`. `view: 'org'` returns 403
   * for a non-admin rather than quietly falling back to their own contacts.
   */
  const contactsList = async (request: CrmContactsListRequest): Promise<CrmContactsListResponse> => {
    const url = `${baseUrl}/contacts/list`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactsListResponse;
  };

  /** Cross-agent duplicate roll-up for the agency view. Org admins only. */
  const contactsLeadsBoard = async (request: CrmContactsListRequest): Promise<CrmLeadsBoardResponse> => {
    const url = `${baseUrl}/contacts/leads-board`;
    return (await sendWebotRequest(url, "POST", request)) as CrmLeadsBoardResponse;
  };

  /**
   * ⚠️ CALL `useCrmHolders()` INSTEAD of this, from UI code. It batches one request per
   * loaded page and caches per (org, contact); calling this per row would be one HTTP
   * request per avatar stack.
   */
  const contactsHolders = async (request: CrmContactHoldersRequest): Promise<CrmContactHoldersResponse> => {
    const url = `${baseUrl}/contacts/holders`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactHoldersResponse;
  };

  /**
   * ⚠️ CALL `useContactsLookup()` / `useContactLookup()` INSTEAD of this, from UI code —
   * they share one module store and coalesce every phone asked about in a tick into
   * chunked requests. At most `CONTACTS_LOOKUP_MAX_IDS` ids; the server refuses more.
   */
  const contactsLookup = async (contactIds: string[]): Promise<ContactsLookupResponse> => {
    const url = `${baseUrl}/contacts/lookup`;
    return (await sendWebotRequest(url, "POST", { contactIds })) as ContactsLookupResponse;
  };

  /**
   * ⚠️ CALL `useListingSellerContacts()` INSTEAD of this, from UI code — it shares one module
   * store and sends every listing asked about in a tick together. At most
   * `CONTACTS_LISTING_SELLERS_MAX_IDS` ids; the server refuses more.
   */
  const contactsListingSellers = async (listingIds: string[]): Promise<ContactsListingSellersResponse> => {
    const url = `${baseUrl}/contacts/listing-sellers`;
    return (await sendWebotRequest(url, "POST", { listingIds })) as ContactsListingSellersResponse;
  };

  /**
   * Which of these phones the caller already has — exists / has a name / blocked for bulk
   * messaging. An import's comparison; at most `CONTACTS_PRESENCE_MAX_IDS` ids per call.
   */
  const contactsPresence = async (contactIds: string[]): Promise<ContactsPresenceResponse> => {
    const url = `${baseUrl}/contacts/presence`;
    return (await sendWebotRequest(url, "POST", { contactIds })) as ContactsPresenceResponse;
  };

  /**
   * Which book the caller's contacts live in — their own email, or `org:<orgId>` for a
   * pooled agency, with the agency's mode (plan D4). Zero I/O server-side.
   *
   * ⚠️ CALL `useContactBook()` INSTEAD of this, from UI code — one shared answer per
   * session is what every document reader and signal listener keys off.
   */
  const contactsBook = async (): Promise<REST_schema.CrmContactBookResponse> => {
    const url = `${baseUrl}/contacts/book`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.CrmContactBookResponse;
  };

  /**
   * "My contacts" inside a POOLED agency — put a member on a person's holder list, or take
   * them off. Self by default; naming somebody else needs an admin. 400 on a personal book,
   * 404 when the office has no record, 503 `CRM_BOOK_BUSY` while the switch runs.
   *
   * ⚠️ CALL `useContactWrites().addContactHolder` / `removeContactHolder` INSTEAD — the
   * hook also tells the local stores the list changed.
   */
  const contactsHoldersAdd = async (
    request: REST_schema.CrmContactHolderChangeRequest
  ): Promise<REST_schema.CrmContactHolderAddResponse> => {
    const url = `${baseUrl}/contacts/holders/add`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmContactHolderAddResponse;
  };

  const contactsHoldersRemove = async (
    request: REST_schema.CrmContactHolderChangeRequest
  ): Promise<REST_schema.CrmContactHolderRemoveResponse> => {
    const url = `${baseUrl}/contacts/holders/remove`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmContactHolderRemoveResponse;
  };

  /** The home page's contact tiles — four counts and the newest unnamed contacts. */
  const contactsSummary = async (): Promise<ContactsSummaryResponse> => {
    const url = `${baseUrl}/contacts/summary`;
    return (await sendWebotRequest(url, "POST", {})) as ContactsSummaryResponse;
  };

  /**
   * The caller's tag vocabulary — every distinct tag on the live contacts of their own book
   * and how many carry it, most used first (the server caps the list at 500; `limit` only
   * lowers it). One statement over the Postgres mirror per call and nothing stored, so a tag
   * disappears from the answer by itself when no contact carries it any more.
   *
   * ⚠️ CALL `useContactTagSuggestions()` INSTEAD of this, from UI code — it asks once per
   * opening of the tag field and drops an answer whose opening has ended.
   */
  const contactsTags = async (
    request: REST_schema.CrmContactTagsRequest = {}
  ): Promise<REST_schema.CrmContactTagsResponse> => {
    const url = `${baseUrl}/contacts/tags`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmContactTagsResponse;
  };

  /**
   * ⚠️ CALL `useCrmMatchesCounts()` INSTEAD of this, from UI code — it batches one
   * request per loaded page and caches per (owner, contact) pair, exactly like holders.
   */
  /**
   * ⚠️ CALL `useCrmAssignments()` / `useContactHandler()` INSTEAD of this, from UI code —
   * they batch one request per loaded page and share a module cache keyed by
   * (orgId, contactId), so the CRM list, the new-contact modal, the pickers and the panel
   * all read one answer instead of asking four times.
   */
  const contactsAssignments = async (
    request: REST_schema.CrmContactAssignmentsRequest
  ): Promise<REST_schema.CrmContactAssignmentsResponse> => {
    const url = `${baseUrl}/contacts/assignments`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmContactAssignmentsResponse;
  };

  /**
   * Designate (or clear) the מתווך מטפל for one contact, and copy it into the assignee's
   * book when they have none.
   *
   * On the ORG router, not `/contacts`, because its gate is "are you an admin of this
   * agency" (plus the outgoing handler, who may hand off) rather than a contacts read
   * scope — see the server controller. `assigneeUserId: null` removes the designation.
   */
  const crmAssignContactHandler = async (
    request: REST_schema.CrmAssignContactRequest
  ): Promise<REST_schema.CrmAssignContactResponse> => {
    const url = `${baseUrl}/crm/org/contacts/assign`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmAssignContactResponse;
  };

  /**
   * The bulk form of the above — one מתווך מטפל for a whole selection, from the bulk bar.
   *
   * Same router, same gate, same per-contact core server-side. The difference is in what a
   * failure looks like: a contact the server could not assign is a `failed` entry in
   * `results`, not a thrown error, so the promise rejects only for request-level refusals
   * (`CRM_SCOPE_*`, a malformed body, the 200-contact cap).
   */
  const crmAssignContactHandlersBulk = async (
    request: REST_schema.CrmAssignContactsBulkRequest
  ): Promise<REST_schema.CrmAssignContactsBulkResponse> => {
    const url = `${baseUrl}/crm/org/contacts/assign-bulk`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmAssignContactsBulkResponse;
  };

  const contactsMatchesCounts = async (
    request: REST_schema.CrmMatchesCountsRequest
  ): Promise<REST_schema.CrmMatchesCountsResponse> => {
    const url = `${baseUrl}/contacts/matches-counts`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmMatchesCountsResponse;
  };

  /**
   * ⚠️ CALL `useCrmListingLinks()` INSTEAD of this, from UI code — it batches per page
   * and caches per listing id (summaries are shared across rows).
   */
  const contactsListingLinks = async (
    request: REST_schema.CrmListingLinksRequest
  ): Promise<REST_schema.CrmListingLinksResponse> => {
    const url = `${baseUrl}/contacts/listing-links`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmListingLinksResponse;
  };

  /**
   * ⚠️ CALL `useOfficeMinisites()` INSTEAD of this, from UI code — it caches per customer, so the
   * matches tab and the side panel ask once.
   */
  const contactsOfficeMinisites = async (
    request: REST_schema.CrmOfficeMinisitesRequest
  ): Promise<REST_schema.CrmOfficeMinisitesResponse> => {
    const url = `${baseUrl}/contacts/office-minisites`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmOfficeMinisitesResponse;
  };

  /**
   * ⚠️ CALL `useContactDocument()` / `useContactByPhone()` INSTEAD of this, from UI code —
   * they share the document store, dedupe requests and refetch on the contact's signal.
   *
   * One contact's whole document and its mirror version: by exact id, or by every spelling
   * a phone could be filed under (`candidates`, in order of preference — the first that
   * exists wins). `found: false` is an answer, not an error. The document crosses the wire
   * with ISO strings where the type says `Date`; it is revived here so a document from the
   * server reads exactly like one the Firestore converter produced.
   */
  const contactsGet = async (
    request: REST_schema.CrmContactGetRequest
  ): Promise<REST_schema.CrmContactGetResponse> => {
    const url = `${baseUrl}/contacts/get`;
    const response = (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmContactGetResponse;
    return response.doc ? { ...response, doc: reviveContactDocument(response.doc) } : response;
  };

  /**
   * The documents behind a list of ids in one book — at most 5,000, refused above (never
   * trimmed: a caller reads a missing id as "not in the book"). `missing` is an answer. The
   * old contacts page's live feed fetches the contacts its signals named through this.
   */
  const contactsGetMany = async (
    request: REST_schema.CrmContactGetManyRequest
  ): Promise<REST_schema.CrmContactGetManyResponse> => {
    const url = `${baseUrl}/contacts/get-many`;
    const response = (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmContactGetManyResponse;
    return {
      ...response,
      contacts: response.contacts.map((entry) => ({ ...entry, doc: reviveContactDocument(entry.doc) }))
    };
  };

  /**
   * Updates one contact — the only contact WRITE the server exposes.
   *
   * Writes Firestore (still the system of record), then drains that one key into the
   * Postgres mirror inline, so the response carries the row as PERSISTED rather than an
   * echo of the patch. Which owner may be written is resolved from `dashboard-orgs`,
   * never taken from the body, and every field is checked against a whitelist.
   */
  const contactsUpdate = async (request: CrmContactUpdateRequest): Promise<CrmContactUpdateResponse> => {
    const url = `${baseUrl}/contacts/update`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactUpdateResponse;
  };

  /**
   * Creates one contact — the create half of `contactsUpdate`, and the reason the browser
   * no longer writes a contact document itself.
   *
   * ⚠️ NOT `useLegacyContactWrites().updateContact`. That one writes `set(merge: true)` from the browser,
   * which silently overwrites a contact already at that number instead of refusing, applies
   * no field whitelist, leaves no audit row, and cannot refresh the Postgres mirror — so the
   * new contact was absent from the CRM page until the next minute's drain tick.
   *
   * Throws on a 409 when the number is taken (live OR soft-deleted). The response's
   * `contactId` is the NORMALIZED one — file the contact under that, not under what was sent.
   */
  const contactsCreate = async (
    request: CrmContactCreateRequest
  ): Promise<CrmContactCreateResponse> => {
    const url = `${baseUrl}/contacts/create`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactCreateResponse;
  };

  /**
   * The file import's write path — an upsert of at most
   * `REST_schema.MAX_BULK_CREATE_CONTACTS` rows per call, so the uploader chunks.
   *
   * Rows fail INDIVIDUALLY: a 200 does not mean every row landed. Read `results` and render
   * the failures, or an import silently drops the handful of unparseable phones every real
   * sheet contains.
   */
  const contactsBulkCreate = async (
    request: CrmContactsBulkCreateRequest
  ): Promise<CrmContactsBulkCreateResponse> => {
    const url = `${baseUrl}/contacts/bulk-create`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactsBulkCreateResponse;
  };

  /**
   * Changes a contact's phone number — a server-side RE-KEY of the document (the doc
   * id IS the phone) plus a port of every satellite. See the contract notes on
   * `WebotServerContextProps.contactsChangePhone`; the UI flow lives in
   * `ChangePhoneDialog`.
   */
  const contactsChangePhone = async (
    request: REST_schema.ChangeContactPhoneRequest
  ): Promise<REST_schema.ChangeContactPhoneResponse> => {
    const url = `${baseUrl}/contacts/change-phone`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.ChangeContactPhoneResponse;
  };

  /**
   * Records the outcome of ONE bulk WhatsApp send, server-side.
   *
   * The migration off `useLegacyContactWrites().updateContact` for this stamp. That wrapper reads the
   * in-browser `allContacts` array to decide between its update and CREATE branches, so
   * on the CRM page — which deliberately never loads that array — it takes the create
   * branch and mints a phantom contact. This endpoint has no create branch at all: a
   * missing document is a 404.
   */
  const contactsRecordBulkMessage = async (
    request: CrmRecordBulkMessageRequest
  ): Promise<CrmRecordBulkMessageResponse> => {
    const url = `${baseUrl}/contacts/record-bulk-message`;
    return (await sendWebotRequest(url, "POST", request)) as CrmRecordBulkMessageResponse;
  };

  /**
   * Soft-deletes a selection.
   *
   * Replaces a browser-side HARD delete of up to 499 documents at a time with no audit and
   * no recovery. Here the server writes `deletedAt`/`deletedBy`, mirrors it, and archives
   * the last-known document in the audit ledger — which is the ONLY recovery path that
   * exists, since Firestore has PITR disabled on this project.
   *
   * A permission or drift failure fails the WHOLE request: a partially-applied destructive
   * action is the worst thing to have to report.
   */
  const contactsDelete = async (request: CrmContactsDeleteRequest): Promise<CrmContactsDeleteResponse> => {
    const url = `${baseUrl}/contacts/delete`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactsDeleteResponse;
  };

  /**
   * One patch, applied to a whole selection.
   *
   * The server runs it through the SAME field whitelist as the single write, so an
   * unknown key is a 400 rather than a silently dropped field. The patch REPLACES what it
   * writes and empty values are dropped — see `CrmContactsBulkUpdateRequest` for why that
   * asymmetry is what keeps a bulk edit from wiping 2,000 contacts' tags.
   */
  const contactsBulkUpdate = async (
    request: CrmContactsBulkUpdateRequest
  ): Promise<CrmContactsBulkUpdateResponse> => {
    const url = `${baseUrl}/contacts/bulk-update`;
    return (await sendWebotRequest(url, "POST", request)) as CrmContactsBulkUpdateResponse;
  };

  /**
   * Exports a selection as CSV.
   *
   * ── Why this does NOT go through `sendWebotRequest` ──────────────────────────────
   *
   * That helper throws `returned non-JSON` on any body whose content-type is not
   * `application/json`, which is exactly what a successful export is. Routing this
   * through it would turn every SUCCESSFUL export into an error and every real failure
   * into the same one — indistinguishable.
   *
   * So it repeats the auth header and the error shape deliberately, and keeps one thing
   * the shared helper does that matters here: on a FAILURE the body IS JSON (the CRM
   * error envelope), so the refusal is parsed into a `WebotServerError` carrying the
   * server's `code`. The export gate (`permissions.export`) and the row cap both refuse
   * through that path, and the UI has specific Hebrew copy for each.
   */
  const contactsExportCsv = async (request: CrmContactsExportRequest): Promise<CrmContactsExportFile> => {
    const url = `${baseUrl}/contacts/export`;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (user?.id) {
      const token = await user.getIdToken();
      if (token) headers.Authorization = token;
    }

    const response = await fetch(url, { method: "POST", body: JSON.stringify(request), headers });

    if (!response.ok) {
      const text = await response.text();
      const contentType = response.headers.get("content-type") ?? "";
      // The same reader `sendWebotRequest` uses — nulls for a body that is not a JSON object.
      const { code, serverMessage, details } = readWebotErrorBody(contentType, text);
      throw new WebotServerError(
        `Webot POST ${url} failed [status=${response.status}] [body="${text.slice(0, 200).replace(/\s+/g, " ").trim()}"]`,
        response.status,
        code,
        serverMessage,
        details
      );
    }

    // `filename="webot-contacts-2026-08-19.xlsx"`. Falling back to a constructed name
    // rather than failing: a download that lands with a plausible name is strictly better
    // than an error, and the only way to lose the header is a proxy stripping it.
    const disposition = response.headers.get("content-disposition") ?? "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : `webot-contacts-${new Date().toISOString().slice(0, 10)}.xlsx`;
    const exported = Number(response.headers.get("x-contacts-exported"));

    // ⚠️ `.blob()`, never `.text()`. The body is an .xlsx — a ZIP container — so decoding
    // it as UTF-8 text and re-encoding it corrupts the archive, and the file that lands
    // does not open at all.
    const blob = await response.blob();
    // `rowCount` comes off the header rather than being counted here: the server already
    // knows it, and nothing in a workbook is cheaper to count than to be told.
    return { blob, filename, rowCount: Number.isFinite(exported) ? exported : 0 };
  };

  // --- CRM organization permissions ----------------------------------------

  /**
   * The agency surface in one call: the caller's own resolved permissions, the
   * agency's caps, the role defaults, and — for an admin only — the roster.
   *
   * READS ARE OPEN TO EVERY MEMBER. A plain agent gets `isAdmin: false` and
   * `members: null` rather than a 403: "org user should see their permissions" is the
   * point, and a refusal would leave the agency-pool UI with no way to explain why it
   * is empty. Only "you are in no agency" (or not in the one you named) is a 403.
   *
   * `orgId` is optional: the server resolves it when the caller belongs to exactly one
   * org, which is nearly everyone — measured 2026-08-16 across 96 orgs, 287 users are
   * in any org at all and the median org is 2 people. Multi-org callers get 400
   * CRM_SCOPE_AMBIGUOUS and the screen asks which agency they meant.
   */
  const crmOrgOverview = async (orgId?: string): Promise<CrmOrgOverviewResponse> => {
    const url = orgId
      ? `${baseUrl}/crm/org/overview?orgId=${encodeURIComponent(orgId)}`
      : `${baseUrl}/crm/org/overview`;
    return (await sendWebotRequest(url, "GET")) as CrmOrgOverviewResponse;
  };

  /**
   * Saves the agency's two caps. The body is a PARTIAL — the screen sends one changed
   * field per request so a failed save reverts exactly one control rather than rolling
   * back a form the user did not touch. Unknown keys are REJECTED by the server (400),
   * never dropped, so a stale field name surfaces immediately instead of as a save
   * that reports success and changes nothing.
   */
  const crmOrgUpdateSettings = async (
    request: CrmOrgSettingsUpdateRequest
  ): Promise<CrmOrgSettings> => {
    const url = `${baseUrl}/crm/org/settings`;
    return (await sendWebotRequest(url, "POST", request)) as CrmOrgSettings;
  };

  /**
   * Saves one member's permissions. Partial, and returns the RESOLVED row — including
   * `configured: false` when the saved set turned out to equal the role default and
   * the server therefore deleted the entry.
   *
   * No `role` field exists: promoting someone is editing `dashboard-orgs.admins`,
   * which this endpoint does not touch.
   */
  const crmOrgUpdateMember = async (
    request: CrmOrgMemberUpdateRequest
  ): Promise<CrmOrgMemberView> => {
    const url = `${baseUrl}/crm/org/members/update`;
    return (await sendWebotRequest(url, "POST", request)) as CrmOrgMemberView;
  };

  /**
   * The bulk form of the above: one patch (or `reset: true`) over several members, in ONE
   * server transaction. Rejects for the whole request — a target who left the agency, an
   * ambiguous org, the 200-target cap — and never for part of it, so a resolved promise
   * means every returned row is what the server now holds.
   */
  const crmOrgUpdateMembersBulk = async (
    request: CrmOrgMembersBulkUpdateRequest
  ): Promise<CrmOrgMembersBulkUpdateResponse> => {
    const url = `${baseUrl}/crm/org/members/update-bulk`;
    return (await sendWebotRequest(url, "POST", request)) as CrmOrgMembersBulkUpdateResponse;
  };

  /**
   * Promote or demote one member. Rejects with the server's reason for the last admin, a
   * non-admin caller, an ambiguous org or a target who is not a member; resolves with the
   * member's row after the write.
   */
  const crmOrgUpdateMemberRole = async (
    request: CrmOrgMemberRoleUpdateRequest
  ): Promise<CrmOrgMemberView> => {
    const url = `${baseUrl}/crm/org/members/role`;
    return (await sendWebotRequest(url, "POST", request)) as CrmOrgMemberView;
  };

  /**
   * The pooled-book switch (plan D4). Six calls, one service: the state, a read-only
   * preview that mints the confirm token, the merge itself (runs inside the request and
   * resumes from its cursor if the request drops), the member sync that keeps a pooled
   * office in line with its roster, the progress poll, and the reversal. Admin-only and
   * allowlisted server-side — a 403 here is the normal answer for most of the fleet, and
   * `useCrmOrgPool` reads it as "no card".
   *
   * ⚠️ CALL `useCrmOrgPool()` INSTEAD of these, from UI code.
   */
  const orgQuery = (orgId?: string): string => (orgId ? `?orgId=${encodeURIComponent(orgId)}` : "");

  const crmOrgPool = async (orgId?: string): Promise<REST_schema.CrmOrgPoolStateResponse> => {
    const url = `${baseUrl}/crm/org/pool${orgQuery(orgId)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.CrmOrgPoolStateResponse;
  };

  const crmOrgPoolPreview = async (
    request: REST_schema.CrmOrgPoolPreviewRequest
  ): Promise<REST_schema.CrmOrgPoolPreviewResponse> => {
    const url = `${baseUrl}/crm/org/pool/preview`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmOrgPoolPreviewResponse;
  };

  const crmOrgPoolMigrate = async (
    request: REST_schema.CrmOrgPoolMigrateRequest
  ): Promise<REST_schema.CrmOrgPoolMigrateResponse> => {
    const url = `${baseUrl}/crm/org/pool/migrate`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmOrgPoolMigrateResponse;
  };

  const crmOrgPoolMembersSync = async (
    request: REST_schema.CrmOrgPoolSyncMembersRequest
  ): Promise<REST_schema.CrmOrgPoolSyncMembersResponse> => {
    const url = `${baseUrl}/crm/org/pool/members/sync`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmOrgPoolSyncMembersResponse;
  };

  const crmOrgPoolProgress = async (orgId?: string): Promise<REST_schema.CrmOrgPoolProgressResponse> => {
    const url = `${baseUrl}/crm/org/pool/progress${orgQuery(orgId)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.CrmOrgPoolProgressResponse;
  };

  const crmOrgPoolReverse = async (
    request: REST_schema.CrmOrgPoolReverseRequest
  ): Promise<REST_schema.CrmOrgPoolReverseResponse> => {
    const url = `${baseUrl}/crm/org/pool/reverse`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.CrmOrgPoolReverseResponse;
  };

  /**
   * The colleague directory: name, avatar, email, admin-or-not, for every member of the
   * caller's agency (or agencies).
   *
   * OPEN TO EVERY MEMBER, unlike `crmOrgOverview` above — which withholds its roster from
   * non-admins because it carries per-member `permissions`. This one carries none, so it
   * is safe for the audience that actually needs it: any screen rendering a teammate's
   * name or face. It also never 403s or 400s, so there is no error state to classify.
   *
   * ⚠️ CALL `useOrgDirectory()` INSTEAD of this, from UI code. The directory is fetched
   * once per session and cached module-side; calling here bypasses that and issues a fresh
   * request per component, which is exactly the N-fetch problem this replaced.
   */
  const orgDirectory = async (orgId?: string): Promise<OrgDirectoryResponse> => {
    const url = orgId
      ? `${baseUrl}/crm/org/directory?orgId=${encodeURIComponent(orgId)}`
      : `${baseUrl}/crm/org/directory`;
    return (await sendWebotRequest(url, "GET")) as OrgDirectoryResponse;
  };

  const getContactDetails = async (
    phoneNumberInternational: string,
    dataSources?: REST_schema.ContactDetailsDataSource[]
  ) => {
    const url = `${baseUrl}/contacts/get-details`;
    const body: REST_schema.ContactDetailsRequestSchema = { phoneNumberInternational };
    if (dataSources) body.dataSources = dataSources;
    return await sendWebotRequest(url, "POST", body);
  };

  const getUpdateContactWebhookForCustomer =
    async (): Promise<REST_schema.GetUpdateContactWebhookForCustomerResponse> => {
      const url = `${baseUrl}/getUpdateContactWebhookForCustomer`;
      return (await sendWebotRequest(
        url,
        "GET"
      )) as REST_schema.GetUpdateContactWebhookForCustomerResponse;
    };

  /** A new secret address for the update-contact webhook; the previous one stops working at once. */
  const rotateUpdateContactWebhookKey =
    async (): Promise<REST_schema.RotateUpdateContactWebhookKeyResponse> => {
      const url = `${baseUrl}/update-contact-webhook/rotate`;
      return (await sendWebotRequest(url, "POST")) as REST_schema.RotateUpdateContactWebhookKeyResponse;
    };

  const generateCreative = async (
    request: REST_schema.GenerateCreativeRequest
  ): Promise<REST_schema.GenerateCreativeResponse> => {
    const url = `${baseUrl}/generateCreative`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.GenerateCreativeResponse;
  };

  const generateMakeovers = async (
    request: REST_schema.GenerateMakeoversRequest
  ): Promise<REST_schema.GenerateMakeoversResponse> => {
    const url = `${baseUrl}/generateMakeovers`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.GenerateMakeoversResponse;
  };

  const setMakeoverShowcase = async (
    request: REST_schema.SetMakeoverShowcaseRequest
  ): Promise<REST_schema.SetMakeoverShowcaseResponse> => {
    const url = `${baseUrl}/setMakeoverShowcase`;
    return (await sendWebotRequest(
      url,
      "POST",
      request
    )) as REST_schema.SetMakeoverShowcaseResponse;
  };

  /**
   * "Edit once, every minisite": stores the realtor's presentation of this listing AND applies it
   * to every one of their minisites containing it — so a buyer holding a link already sent sees the
   * change on their next load. Minisites with a deliberate one-off exception are left alone.
   */
  const saveListingPresentation = async (
    request: REST_schema.SaveListingPresentationRequest
  ): Promise<REST_schema.SaveListingPresentationResponse> => {
    const url = `${baseUrl}/listing-presentations/save`;
    const response = (await sendWebotRequest(url, "POST", request)) as REST_schema.SaveListingPresentationResponse;
    // The next add of this listing carries what was just saved, not what an earlier add looked up.
    forgetListingPresentationForAdd(request.listingId);
    return response;
  };

  /**
   * The realtor's presentation of a listing, plus how many customers it reaches. When no global
   * record exists yet the values are seeded from their existing per-contact edits — which is what
   * stops the first save from silently un-hiding photos they had hidden for one buyer.
   */
  const getListingPresentation = async (
    listingId: string
  ): Promise<REST_schema.GetListingPresentationResponse> => {
    const url = `${baseUrl}/listing-presentations/${encodeURIComponent(listingId)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.GetListingPresentationResponse;
  };

  const getWebotListingMakeovers = async (
    realtorMail: string,
    listingIds: string[]
  ): Promise<REST_schema.PublicListingMakeoversResponse> => {
    const url = `${baseUrl}/public/listing-makeovers`;
    return (await sendWebotRequest(url, "POST", {
      realtorMail,
      listingIds
    })) as REST_schema.PublicListingMakeoversResponse;
  };

  const deleteMakeover = async (
    request: REST_schema.DeleteMakeoverRequest
  ): Promise<REST_schema.DeleteMakeoverResponse> => {
    const url = `${baseUrl}/deleteMakeover`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.DeleteMakeoverResponse;
  };

  const removeListingWatermarks = async (
    request: REST_schema.RemoveListingWatermarksRequest
  ): Promise<REST_schema.RemoveListingWatermarksResponse> => {
    const url = `${baseUrl}/removeListingWatermarks`;
    return (await sendWebotRequest(
      url,
      "POST",
      request
    )) as REST_schema.RemoveListingWatermarksResponse;
  };

  const getMcpConnectionInfo = async (): Promise<REST_schema.McpConnectionInfoResponse> => {
    const url = `${baseUrl}/mcp/connection-info`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.McpConnectionInfoResponse;
  };

  // --- Agency-ambassador offer (personal upsell, until 2026-12-01). The GET answers
  //     `{ offer: null }` for everyone not on the approved list; the POST is idempotent
  //     server-side (one user-feedbacks record + one Slack lead per customer). ---
  const getAgencyOffer = async (): Promise<REST_schema.AgencyOfferResponse> => {
    const url = `${baseUrl}/agency-offer`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AgencyOfferResponse;
  };

  const registerAgencyOfferInterest = async (
    request: REST_schema.AgencyOfferInterestRequest
  ): Promise<REST_schema.AgencyOfferInterestResponse> => {
    const url = `${baseUrl}/agency-offer/interest`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.AgencyOfferInterestResponse;
  };

  // --- Contacts: precomputed "smart defaults" for the new/edit-contact modal.
  //     Single endpoint — the server serves its cache and recomputes when stale. ---
  const getContactSmartDefaults = async (): Promise<REST_schema.GetContactSmartDefaultsResponse> => {
    const url = `${baseUrl}/contacts/smart-defaults`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.GetContactSmartDefaultsResponse;
  };

  // --- Coins ---
  const getMyCoinBalance = async (): Promise<CoinBalanceBreakdown> => {
    const url = `${baseUrl}/coins/balance`;
    return (await sendWebotRequest(url, "GET")) as CoinBalanceBreakdown;
  };

  // Centralized admin search (autocomplete). Reusable beyond coins.
  const adminSearchUsers = async (search: string): Promise<REST_schema.AdminUserSearchResponse> => {
    const url = `${baseUrl}/admin/users/search?search=${encodeURIComponent(search)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminUserSearchResponse;
  };

  const adminSearchOrgs = async (search: string): Promise<REST_schema.AdminOrgSearchResponse> => {
    const url = `${baseUrl}/admin/orgs/search?search=${encodeURIComponent(search)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminOrgSearchResponse;
  };

  // --- Admin users screen (user management) ---
  const adminGetUserProfile = async (userId: string): Promise<REST_schema.AdminUserProfileResponse> => {
    const url = `${baseUrl}/admin/users/${encodeURIComponent(userId)}/profile`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminUserProfileResponse;
  };

  const adminGetUserStats = async (userId: string): Promise<REST_schema.AdminUserStatsResponse> => {
    const url = `${baseUrl}/admin/users/${encodeURIComponent(userId)}/stats`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminUserStatsResponse;
  };

  const adminUpdateUser = async (request: REST_schema.AdminUpdateUserRequest): Promise<REST_schema.AdminUpdateUserResponse> => {
    const url = `${baseUrl}/admin/users/update`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.AdminUpdateUserResponse;
  };

  // --- DB explorer (owner-only Postgres console, /dashboard/admin/db) ---
  // Own request helper (not sendWebotRequest) so the server's pg diagnostics
  // (SQLSTATE, detail, hint, position) survive into the thrown Error message —
  // a DBA console must show the real database error, not a flattened one.
  const sendDbExplorerRequest = async (path: string, method: "GET" | "POST", body?: unknown): Promise<any> => {
    const userIdToken = user?.id ? await user.getIdToken() : undefined;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (userIdToken) headers.Authorization = userIdToken;
    const response = await fetch(`${baseUrl}/admin/db${path}`, {
      method,
      body: method === "POST" ? JSON.stringify(body ?? {}) : undefined,
      headers
    });
    const text = await response.text();
    let parsed: any;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = undefined;
    }
    if (!response.ok) {
      const err = (parsed ?? {}) as REST_schema.DbExplorerErrorResponse;
      const parts: string[] = [err.error || `HTTP ${response.status}`];
      if (err.code) parts.push(`[${err.code}]`);
      if (err.detail) parts.push(`— ${err.detail}`);
      if (err.hint) parts.push(`(hint: ${err.hint})`);
      if (err.position) parts.push(`(at char ${err.position})`);
      throw new Error(parts.join(" "));
    }
    return parsed;
  };

  const dbExplorerInfo = async (): Promise<REST_schema.DbExplorerInfoResponse> =>
    (await sendDbExplorerRequest("/info", "GET")) as REST_schema.DbExplorerInfoResponse;

  const dbExplorerListTables = async (
    db: REST_schema.DbExplorerDbKey
  ): Promise<REST_schema.DbExplorerTablesResponse> =>
    (await sendDbExplorerRequest(`/${db}/tables`, "GET")) as REST_schema.DbExplorerTablesResponse;

  const dbExplorerTableSchema = async (
    db: REST_schema.DbExplorerDbKey,
    table: string
  ): Promise<REST_schema.DbExplorerTableSchemaResponse> =>
    (await sendDbExplorerRequest(
      `/${db}/tables/${encodeURIComponent(table)}/schema`,
      "GET"
    )) as REST_schema.DbExplorerTableSchemaResponse;

  const dbExplorerBrowseRows = async (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerBrowseRequest
  ): Promise<REST_schema.DbExplorerBrowseResponse> =>
    (await sendDbExplorerRequest(
      `/${db}/tables/${encodeURIComponent(table)}/rows`,
      "POST",
      request
    )) as REST_schema.DbExplorerBrowseResponse;

  const dbExplorerInsertRow = async (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerInsertRequest
  ): Promise<REST_schema.DbExplorerMutationResponse> =>
    (await sendDbExplorerRequest(
      `/${db}/tables/${encodeURIComponent(table)}/rows/insert`,
      "POST",
      request
    )) as REST_schema.DbExplorerMutationResponse;

  const dbExplorerUpdateRow = async (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerUpdateRequest
  ): Promise<REST_schema.DbExplorerMutationResponse> =>
    (await sendDbExplorerRequest(
      `/${db}/tables/${encodeURIComponent(table)}/rows/update`,
      "POST",
      request
    )) as REST_schema.DbExplorerMutationResponse;

  const dbExplorerDeleteRow = async (
    db: REST_schema.DbExplorerDbKey,
    table: string,
    request: REST_schema.DbExplorerDeleteRequest
  ): Promise<REST_schema.DbExplorerMutationResponse> =>
    (await sendDbExplorerRequest(
      `/${db}/tables/${encodeURIComponent(table)}/rows/delete`,
      "POST",
      request
    )) as REST_schema.DbExplorerMutationResponse;

  const dbExplorerRunSql = async (
    db: REST_schema.DbExplorerDbKey,
    request: REST_schema.DbExplorerSqlRequest
  ): Promise<REST_schema.DbExplorerSqlResponse> =>
    (await sendDbExplorerRequest(`/${db}/query`, "POST", request)) as REST_schema.DbExplorerSqlResponse;

  // --- Sales map (מפת משרדים, owner-only — gated on SALES_MAP_ALLOWED_EMAILS) + live GoHighLevel panel ---
  // Own request helper (not sendWebotRequest / sendDbExplorerRequest): the server's
  // SalesMapErrorResponse carries `code` and, on a 409 DUPLICATE, the existing GHL
  // `contact` — both need to survive as typed fields on the thrown error, not just text.
  const sendSalesMapRequest = async (path: string, method: "GET" | "POST", body?: unknown): Promise<any> => {
    const userIdToken = user?.id ? await user.getIdToken() : undefined;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (userIdToken) headers.Authorization = userIdToken;
    const response = await fetch(`${baseUrl}/admin/sales-map${path}`, {
      method,
      body: method === "POST" ? JSON.stringify(body ?? {}) : undefined,
      headers
    });
    const text = await response.text();
    let parsed: any;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = undefined;
    }
    if (!response.ok) {
      const err = (parsed ?? {}) as REST_schema.SalesMapErrorResponse;
      throw new SalesMapError(err.error || `HTTP ${response.status}`, response.status, err.code, err.contact);
    }
    return parsed;
  };

  // The snapshot is served gzip-encoded (`Content-Encoding: gzip`); the browser decodes it
  // transparently, so this is a plain fetch + json(), not sendSalesMapRequest.
  const salesMapGetSnapshot = async (): Promise<REST_schema.SalesMapSnapshot> => {
    const userIdToken = user?.id ? await user.getIdToken() : undefined;
    const headers: Record<string, string> = {};
    if (userIdToken) headers.Authorization = userIdToken;
    const response = await fetch(`${baseUrl}/admin/sales-map/snapshot`, { headers });
    if (!response.ok) {
      let parsed: Partial<REST_schema.SalesMapErrorResponse> = {};
      try {
        parsed = await response.json();
      } catch {
        parsed = {};
      }
      throw new SalesMapError(parsed.error || `HTTP ${response.status}`, response.status, parsed.code, parsed.contact);
    }
    return (await response.json()) as REST_schema.SalesMapSnapshot;
  };

  // --- Sales training portal (owner-only — gated on SALES_TRAINING_ALLOWED_EMAILS) ---
  // A fresh ID token on every call (getIdToken refreshes it). The page is served gzip-encoded and the browser decodes
  // it; audio comes back as raw bytes for the portal frame (it can't fetch on its own — it has no credentials).
  const sendSalesTrainingRequest = async (path: string): Promise<Response> => {
    const userIdToken = user?.id ? await user.getIdToken() : undefined;
    const headers: Record<string, string> = {};
    if (userIdToken) headers.Authorization = userIdToken;
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/admin/sales-training${path}`, { headers });
    } catch (error) {
      throw new SalesTrainingError((error as Error)?.message || "Network error", 0, null);
    }
    if (!response.ok) {
      let parsed: Partial<REST_schema.SalesTrainingErrorResponse> = {};
      try {
        parsed = await response.json();
      } catch {
        parsed = {};
      }
      throw new SalesTrainingError(parsed.error || `HTTP ${response.status}`, response.status, parsed.code);
    }
    return response;
  };

  const salesTrainingGetManifest = async (): Promise<REST_schema.SalesTrainingManifest> =>
    (await (await sendSalesTrainingRequest("/manifest")).json()) as REST_schema.SalesTrainingManifest;

  const salesTrainingGetPage = async (): Promise<string> => (await sendSalesTrainingRequest("/page")).text();

  const salesTrainingGetAudio = async (name: string): Promise<ArrayBuffer> =>
    (await sendSalesTrainingRequest(`/audio/${encodeURIComponent(name)}`)).arrayBuffer();

  // --- Sales onboarding portal v2 (the owners + the readers they add from the page) ---
  // The server's `code` (INVALID / OWNER / LIMIT / NOT_READY / FORBIDDEN) survives on the thrown SalesTrainingError.
  const sendSalesTrainingV2Request = async (path: string, method: "GET" | "POST" = "GET", body?: unknown): Promise<Response> => {
    const userIdToken = user?.id ? await user.getIdToken() : undefined;
    const headers: Record<string, string> = {};
    if (userIdToken) headers.Authorization = userIdToken;
    if (method === "POST") headers["Content-Type"] = "application/json";
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/admin/sales-training-v2${path}`, {
        method,
        headers,
        body: method === "POST" ? JSON.stringify(body ?? {}) : undefined
      });
    } catch (error) {
      throw new SalesTrainingError((error as Error)?.message || "Network error", 0, null);
    }
    if (!response.ok) {
      let parsed: Partial<REST_schema.SalesTrainingV2ErrorResponse> = {};
      try {
        parsed = await response.json();
      } catch {
        parsed = {};
      }
      throw new SalesTrainingError(parsed.error || `HTTP ${response.status}`, response.status, parsed.code);
    }
    return response;
  };

  const salesTrainingV2GetAccess = async (): Promise<REST_schema.SalesTrainingV2Access> =>
    (await (await sendSalesTrainingV2Request("/me")).json()) as REST_schema.SalesTrainingV2Access;

  const salesTrainingV2GetPage = async (): Promise<string> => (await sendSalesTrainingV2Request("/page")).text();

  const salesTrainingV2ListViewers = async (): Promise<REST_schema.SalesTrainingV2ViewersResponse> =>
    (await (await sendSalesTrainingV2Request("/viewers")).json()) as REST_schema.SalesTrainingV2ViewersResponse;

  const salesTrainingV2AddViewer = async (email: string): Promise<REST_schema.SalesTrainingV2ViewersResponse> =>
    (await (await sendSalesTrainingV2Request("/viewers", "POST", { email })).json()) as REST_schema.SalesTrainingV2ViewersResponse;

  const salesTrainingV2RemoveViewer = async (email: string): Promise<REST_schema.SalesTrainingV2ViewersResponse> =>
    (await (
      await sendSalesTrainingV2Request("/viewers/remove", "POST", { email })
    ).json()) as REST_schema.SalesTrainingV2ViewersResponse;

  // --- Sales recruiting (מיון מועמדים למכירות, owner-only — gated on SALES_RECRUITING_ADMIN_EMAILS) ---
  // Own request helper, like the sales map's: the server's SalesRecruitingErrorResponse carries a `code`
  // (NOT_ALLOWED: "their link is already with them") that must survive as a typed field on the thrown error.
  const sendSalesRecruitingRequest = async (path: string, method: "GET" | "POST", body?: unknown): Promise<any> => {
    const userIdToken = user?.id ? await user.getIdToken() : undefined;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (userIdToken) headers.Authorization = userIdToken;
    let response: Response;
    try {
      response = await fetch(`${baseUrl}/admin/sales-recruiting${path}`, {
        method,
        body: method === "POST" ? JSON.stringify(body ?? {}) : undefined,
        headers
      });
    } catch (error) {
      throw new SalesRecruitingError((error as Error)?.message || "Network error", 0, null);
    }
    const text = await response.text();
    let parsed: any;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = undefined;
    }
    if (!response.ok) {
      const err = (parsed ?? {}) as REST_schema.SalesRecruitingErrorResponse;
      throw new SalesRecruitingError(err.error || `HTTP ${response.status}`, response.status, err.code);
    }
    return parsed;
  };

  const salesRecruitingList = async (): Promise<REST_schema.SalesRecruitingListResponse> =>
    (await sendSalesRecruitingRequest("/applicants", "GET")) as REST_schema.SalesRecruitingListResponse;

  const salesRecruitingSetReview = async (
    applicantId: string,
    status: REST_schema.SalesRecruitingReviewStatus | null
  ): Promise<REST_schema.SalesRecruitingApplicantResponse> =>
    (await sendSalesRecruitingRequest(`/applicants/${encodeURIComponent(applicantId)}/review`, "POST", {
      status
    })) as REST_schema.SalesRecruitingApplicantResponse;

  const salesRecruitingAddNote = async (
    applicantId: string,
    text: string
  ): Promise<REST_schema.SalesRecruitingApplicantResponse> =>
    (await sendSalesRecruitingRequest(`/applicants/${encodeURIComponent(applicantId)}/notes`, "POST", {
      text
    })) as REST_schema.SalesRecruitingApplicantResponse;

  const salesRecruitingDeleteNote = async (
    applicantId: string,
    noteId: string
  ): Promise<REST_schema.SalesRecruitingApplicantResponse> =>
    (await sendSalesRecruitingRequest(
      `/applicants/${encodeURIComponent(applicantId)}/notes/${encodeURIComponent(noteId)}/delete`,
      "POST"
    )) as REST_schema.SalesRecruitingApplicantResponse;

  // archive | unarchive | send-intro | send-link | give-back-intro
  const salesRecruitingAction = async (
    applicantId: string,
    action: "archive" | "unarchive" | "send-intro" | "send-link" | "give-back-intro"
  ): Promise<REST_schema.SalesRecruitingApplicantResponse> =>
    (await sendSalesRecruitingRequest(
      `/applicants/${encodeURIComponent(applicantId)}/${action}`,
      "POST"
    )) as REST_schema.SalesRecruitingApplicantResponse;

  const salesRecruitingSetAutomation = async (
    enabled: boolean
  ): Promise<REST_schema.SalesRecruitingAutomationResponse> =>
    (await sendSalesRecruitingRequest("/automation", "POST", {
      enabled
    })) as REST_schema.SalesRecruitingAutomationResponse;

  const salesRecruitingRunIntake = async (): Promise<REST_schema.SalesRecruitingAutomationResponse> =>
    (await sendSalesRecruitingRequest("/intake/run", "POST")) as REST_schema.SalesRecruitingAutomationResponse;

  const salesMapGhlContext = async (): Promise<REST_schema.SalesMapGhlContextResponse> =>
    (await sendSalesMapRequest("/ghl/context", "GET")) as REST_schema.SalesMapGhlContextResponse;

  const salesMapGhlLinks = async (): Promise<REST_schema.SalesMapGhlLinksResponse> =>
    (await sendSalesMapRequest("/ghl/links", "GET")) as REST_schema.SalesMapGhlLinksResponse;

  const salesMapGhlLink = async (request: REST_schema.SalesMapGhlLinkRequest): Promise<{ ok: true }> =>
    (await sendSalesMapRequest("/ghl/links", "POST", request)) as { ok: true };

  const salesMapGhlUnlink = async (request: REST_schema.SalesMapGhlLinkRequest): Promise<{ ok: true }> =>
    (await sendSalesMapRequest("/ghl/links/delete", "POST", request)) as { ok: true };

  const salesMapGhlLookup = async (
    request: REST_schema.SalesMapGhlLookupRequest
  ): Promise<REST_schema.SalesMapGhlLookupResponse> =>
    (await sendSalesMapRequest("/ghl/lookup", "POST", request)) as REST_schema.SalesMapGhlLookupResponse;

  const salesMapGhlContact = async (contactId: string): Promise<REST_schema.SalesMapGhlContactResponse> =>
    (await sendSalesMapRequest(`/ghl/contacts/${encodeURIComponent(contactId)}`, "GET")) as REST_schema.SalesMapGhlContactResponse;

  const salesMapGhlAddNote = async (
    contactId: string,
    request: REST_schema.SalesMapGhlAddNoteRequest
  ): Promise<REST_schema.SalesMapGhlAddNoteResponse> =>
    (await sendSalesMapRequest(
      `/ghl/contacts/${encodeURIComponent(contactId)}/notes`,
      "POST",
      request
    )) as REST_schema.SalesMapGhlAddNoteResponse;

  const salesMapGhlCreateContact = async (
    request: REST_schema.SalesMapGhlCreateContactRequest
  ): Promise<REST_schema.SalesMapGhlCreateContactResponse> =>
    (await sendSalesMapRequest("/ghl/contacts", "POST", request)) as REST_schema.SalesMapGhlCreateContactResponse;

  const salesMapGhlSetStage = async (
    contactId: string,
    request: REST_schema.SalesMapGhlSetStageRequest
  ): Promise<REST_schema.SalesMapGhlSetStageResponse> =>
    (await sendSalesMapRequest(
      `/ghl/contacts/${encodeURIComponent(contactId)}/stage`,
      "POST",
      request
    )) as REST_schema.SalesMapGhlSetStageResponse;

  const salesMapGhlAssign = async (
    contactId: string,
    request: REST_schema.SalesMapGhlAssignRequest
  ): Promise<REST_schema.SalesMapGhlAssignResponse> =>
    (await sendSalesMapRequest(
      `/ghl/contacts/${encodeURIComponent(contactId)}/assign`,
      "POST",
      request
    )) as REST_schema.SalesMapGhlAssignResponse;

  const salesMapGhlTags = async (
    contactId: string,
    request: REST_schema.SalesMapGhlTagsRequest
  ): Promise<REST_schema.SalesMapGhlTagsResponse> =>
    (await sendSalesMapRequest(
      `/ghl/contacts/${encodeURIComponent(contactId)}/tags`,
      "POST",
      request
    )) as REST_schema.SalesMapGhlTagsResponse;

  // ---- Location autocomplete (canonical registry; replaces Algolia suggestions) ----
  const locationsAutocomplete = async (
    query: string,
    opts?: {
      resultType?: REST_schema.LocationSuggestionLevel | "any";
      includeEmpty?: boolean;
      includeCoords?: boolean;
      cityCode?: number;
      limit?: number;
      expandStreetNeighborhoods?: boolean;
    }
  ): Promise<REST_schema.LocationsAutocompleteResponse> => {
    const params = new URLSearchParams({ q: query });
    if (opts?.resultType && opts.resultType !== "any") params.set("resultType", opts.resultType);
    if (opts?.includeEmpty != null) params.set("includeEmpty", String(opts.includeEmpty));
    if (opts?.includeCoords) params.set("includeCoords", "true");
    if (opts?.cityCode) params.set("cityCode", String(opts.cityCode));
    if (opts?.limit) params.set("limit", String(opts.limit));
    // Older server deploys simply ignore the param, so sending it is always safe.
    if (opts?.expandStreetNeighborhoods) params.set("expandStreetNeighborhoods", "true");
    return (await sendWebotRequest(`${baseUrl}/locations/autocomplete?${params.toString()}`, "GET")) as REST_schema.LocationsAutocompleteResponse;
  };

  // Corpus-based street → neighborhood inference (auto-fill after a street pick).
  const locationsStreetNeighborhood = async (
    city: string,
    street: string
  ): Promise<StreetNeighborhoodResponse> => {
    const params = new URLSearchParams({ city, street });
    return (await sendWebotRequest(
      `${baseUrl}/locations/street-neighborhood?${params.toString()}`,
      "GET"
    )) as StreetNeighborhoodResponse;
  };

  // ---- Canonical locations admin (registry explorer + corrections) ----
  const adminLocationsOverview = async (): Promise<REST_schema.AdminLocationOverviewResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/overview`, "GET")) as REST_schema.AdminLocationOverviewResponse;
  };

  const adminLocationsListCities = async (
    q?: string,
    limit?: number
  ): Promise<REST_schema.AdminLocationCitiesResponse> => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (limit) params.set("limit", String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return (await sendWebotRequest(`${baseUrl}/admin/locations/cities${suffix}`, "GET")) as REST_schema.AdminLocationCitiesResponse;
  };

  const adminLocationsCityDetail = async (
    cityCode: number
  ): Promise<REST_schema.AdminLocationCityDetailResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/cities/${cityCode}`, "GET")) as REST_schema.AdminLocationCityDetailResponse;
  };

  const adminLocationsGetLocation = async (
    locationId: string
  ): Promise<REST_schema.AdminLocationDetailResponse> => {
    const url = `${baseUrl}/admin/locations/${encodeURIComponent(locationId)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminLocationDetailResponse;
  };

  const adminLocationsSearch = async (
    q: string,
    level?: REST_schema.LocationLevel | "any",
    cityCode?: number
  ): Promise<REST_schema.AdminLocationSearchResponse> => {
    const params = new URLSearchParams({ q });
    if (level && level !== "any") params.set("level", level);
    if (cityCode) params.set("cityCode", String(cityCode));
    return (await sendWebotRequest(`${baseUrl}/admin/locations/search?${params.toString()}`, "GET")) as REST_schema.AdminLocationSearchResponse;
  };

  const adminLocationsResolvePreview = async (
    request: REST_schema.AdminLocationResolvePreviewRequest
  ): Promise<REST_schema.AdminLocationResolvePreviewResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/resolve-preview`, "POST", request)) as REST_schema.AdminLocationResolvePreviewResponse;
  };

  const adminLocationsIssues = async (): Promise<REST_schema.AdminLocationIssuesResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/issues`, "GET")) as REST_schema.AdminLocationIssuesResponse;
  };

  // Reverse lookup: which master(s) does this wording map to, and where is it seen?
  const adminLocationsLookup = async (
    q: string,
    source?: string,
    limit?: number
  ): Promise<REST_schema.AdminLocationLookupResponse> => {
    const params = new URLSearchParams({ q });
    if (source) params.set("source", source);
    if (limit) params.set("limit", String(limit));
    return (await sendWebotRequest(`${baseUrl}/admin/locations/lookup?${params.toString()}`, "GET")) as REST_schema.AdminLocationLookupResponse;
  };

  // Source spelling → master mapping table for one city (optionally one source).
  const adminLocationsSourceMappings = async (
    cityCode: number,
    source?: string,
    limit?: number
  ): Promise<REST_schema.AdminLocationSourceMappingsResponse> => {
    const params = new URLSearchParams({ cityCode: String(cityCode) });
    if (source) params.set("source", source);
    if (limit) params.set("limit", String(limit));
    return (await sendWebotRequest(
      `${baseUrl}/admin/locations/source-mappings?${params.toString()}`,
      "GET"
    )) as REST_schema.AdminLocationSourceMappingsResponse;
  };

  const adminLocationsSampleListings = async (
    locationId: string,
    limit?: number,
    source?: string
  ): Promise<REST_schema.AdminLocationListingsResponse> => {
    const params = new URLSearchParams({ locationId });
    if (limit) params.set("limit", String(limit));
    if (source) params.set("source", source);
    return (await sendWebotRequest(`${baseUrl}/admin/locations/listings?${params.toString()}`, "GET")) as REST_schema.AdminLocationListingsResponse;
  };

  const adminLocationsListOverrides = async (): Promise<REST_schema.AdminLocationOverridesResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/overrides`, "GET")) as REST_schema.AdminLocationOverridesResponse;
  };

  const adminLocationsCreateOverride = async (
    request: REST_schema.CreateLocationOverrideRequest
  ): Promise<REST_schema.CreateLocationOverrideResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/overrides`, "POST", request)) as REST_schema.CreateLocationOverrideResponse;
  };

  const adminLocationsDeleteOverride = async (id: string): Promise<{ ok: boolean }> => {
    const url = `${baseUrl}/admin/locations/overrides/${encodeURIComponent(id)}`;
    return (await sendWebotRequest(url, "DELETE")) as { ok: boolean };
  };

  // --- Unresolved queue, AI proposals, resolution provenance -------------------
  // These read the Postgres listings mirror and are deliberately slow-but-live
  // (seconds, not milliseconds) — a cached queue would show rows already fixed.

  const adminLocationsUnresolved = async (
    city?: string,
    origin?: string,
    limit?: number
  ): Promise<REST_schema.AdminUnresolvedQueueResponse> => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (origin) params.set("origin", origin);
    if (limit) params.set("limit", String(limit));
    return (await sendWebotRequest(
      `${baseUrl}/admin/locations/unresolved?${params.toString()}`,
      "GET"
    )) as REST_schema.AdminUnresolvedQueueResponse;
  };

  const adminLocationsUnresolvedDetail = async (id: string): Promise<REST_schema.AdminUnresolvedDetailResponse> => {
    const params = new URLSearchParams({ id });
    return (await sendWebotRequest(
      `${baseUrl}/admin/locations/unresolved/detail?${params.toString()}`,
      "GET"
    )) as REST_schema.AdminUnresolvedDetailResponse;
  };

  const adminLocationsPropose = async (ids: string[]): Promise<REST_schema.AdminLocationProposeResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/unresolved/propose`, "POST", {
      ids
    })) as REST_schema.AdminLocationProposeResponse;
  };

  const adminLocationsListProposals = async (
    status?: string
  ): Promise<REST_schema.AdminLocationProposalsResponse> => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    return (await sendWebotRequest(
      `${baseUrl}/admin/locations/unresolved/proposals?${params.toString()}`,
      "GET"
    )) as REST_schema.AdminLocationProposalsResponse;
  };

  const adminLocationsApplyProposal = async (
    id: string
  ): Promise<REST_schema.CreateLocationOverrideResponse> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/unresolved/proposals/apply`, "POST", {
      id
    })) as REST_schema.CreateLocationOverrideResponse;
  };

  const adminLocationsDismissProposal = async (id: string): Promise<{ ok: boolean }> => {
    return (await sendWebotRequest(`${baseUrl}/admin/locations/unresolved/proposals/dismiss`, "POST", {
      id
    })) as { ok: boolean };
  };

  const adminLocationsProvenance = async (
    city?: string,
    origin?: string
  ): Promise<REST_schema.AdminLocationProvenanceResponse> => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (origin) params.set("origin", origin);
    return (await sendWebotRequest(
      `${baseUrl}/admin/locations/provenance?${params.toString()}`,
      "GET"
    )) as REST_schema.AdminLocationProvenanceResponse;
  };

  const adminLocationsProvenanceListings = async (filters: {
    method?: string;
    resolvedBy?: string;
    overrideId?: string;
    flag?: string;
    city?: string;
    origin?: string;
    limit?: number;
  }): Promise<REST_schema.AdminLocationProvenanceListingsResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "") params.set(key, String(value));
    });
    return (await sendWebotRequest(
      `${baseUrl}/admin/locations/provenance/listings?${params.toString()}`,
      "GET"
    )) as REST_schema.AdminLocationProvenanceListingsResponse;
  };

  const adminGetOrgCoins = async (
    organizationId: string
  ): Promise<REST_schema.AdminOrgCoinsResponse> => {
    const url = `${baseUrl}/coins/admin/orgs/${encodeURIComponent(organizationId)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminOrgCoinsResponse;
  };

  const adminGetUserCoins = async (
    userId: string
  ): Promise<REST_schema.AdminUserCoinsResponse> => {
    const url = `${baseUrl}/coins/admin/users/${encodeURIComponent(userId)}`;
    return (await sendWebotRequest(url, "GET")) as REST_schema.AdminUserCoinsResponse;
  };

  const adminGrantCoinPack = async (
    request: REST_schema.AdminGrantCoinsRequest
  ): Promise<REST_schema.AdminCoinsMutationResponse> => {
    const url = `${baseUrl}/coins/admin/grant`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.AdminCoinsMutationResponse;
  };

  const adminAdjustCoinPack = async (
    request: REST_schema.AdminAdjustCoinsRequest
  ): Promise<REST_schema.AdminCoinsMutationResponse> => {
    const url = `${baseUrl}/coins/admin/adjust`;
    return (await sendWebotRequest(url, "POST", request)) as REST_schema.AdminCoinsMutationResponse;
  };

  // --- Facebook leads ---

  // ---------------------------------------------------------------- Official WhatsApp
  const whatsappGetStatus = async () => await sendWebotRequest(`${baseUrl}/whatsapp/status`, "GET");

  const whatsappBeginConnect = async (request: { businessName: string; mode?: string }) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/connect`, "POST", request);

  const whatsappCompleteConnect = async (request: {
    code: string;
    wabaId?: string;
    phoneNumberId?: string;
  }) => await sendWebotRequest(`${baseUrl}/whatsapp/connect/complete`, "POST", request);

  const whatsappReportConnectDiagnostics = async (request: WhatsappConnectDiagnostics) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/connect/diagnostics`, "POST", request);

  const whatsappDisconnect = async (request?: { releaseAtMeta?: boolean }) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/disconnect`, "POST", request ?? {});

  /**
   * Asks Meta directly what is blocking the connection and republishes the answer. The result
   * also lands in `whatsapp-tenant-status`, so the live listener re-renders on its own — the
   * return value is only needed to tell the realtor the check finished.
   */
  const whatsappCheckStatus = async () =>
    await sendWebotRequest(`${baseUrl}/whatsapp/status/check`, "POST", {});

  const whatsappGetConversations = async (numberKey: string) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/conversations?numberKey=${encodeURIComponent(numberKey)}`,
      "GET"
    );

  const whatsappGetMessages = async (conversationId: string) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/conversations/${encodeURIComponent(conversationId)}/messages`,
      "GET"
    );

  const whatsappMarkRead = async (conversationId: string) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/conversations/${encodeURIComponent(conversationId)}/read`,
      "POST"
    );

  const whatsappSendMessage = async (conversationId: string, text: string) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/conversations/${encodeURIComponent(conversationId)}/send`,
      "POST",
      { text }
    );

  const whatsappListNumberTemplates = async (numberKey: string) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/numbers/${encodeURIComponent(numberKey)}/templates`,
      "GET"
    );

  // --- Smart assistant (the programmable, tool-less auto-responder on a number) --------------

  /**
   * Dry-runs one assistant turn. Writes nothing, sends nothing — `systemPromptOverride` is how
   * the settings tab tests an UNSAVED draft, so the realtor can iterate without committing.
   */
  const whatsappTestAssistant = async (numberKey: string, request: REST_schema.WhatsappAssistantTestRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/assistant/${encodeURIComponent(numberKey)}/test`,
      "POST",
      request
    )) as REST_schema.WhatsappAssistantTestResponse;

  /**
   * Updates the number's assistant config. The server whitelists the editable fields and refuses
   * an opener template that is not APPROVED on this number, so a bad value comes back as a 400
   * with Hebrew copy rather than a silently dead funnel.
   *
   * The live Firestore listener re-renders on its own; the returned config is only used to keep
   * the form in sync with what was actually stored.
   */
  const whatsappUpdateAssistant = async (numberKey: string, request: REST_schema.WhatsappAssistantUpdateRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/assistant/${encodeURIComponent(numberKey)}`,
      "POST",
      request
    )) as REST_schema.WhatsappAssistantUpdateResponse;

  // --- Sharing a number with the office -------------------------------------------------------

  /**
   * Who the number is shared with, plus the office roster the caller may share it with. The
   * roster is EMPTY for a plain member — `role: null` — and the dialog renders read-only then.
   */
  const whatsappGetNumberSharing = async (numberKey: string) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/numbers/${encodeURIComponent(numberKey)}/sharing`,
      "GET"
    )) as REST_schema.WhatsappNumberSharingView;

  /**
   * Sets the membership to the ticked colleagues. The server decides who may do this (the owner,
   * or an admin of the owner's office) and refuses anyone outside the roster with a Hebrew
   * sentence the dialog shows verbatim. The live `whatsapp-number-access` listener re-renders the
   * switcher and the card on its own; the response is for the receipt line.
   */
  const whatsappUpdateNumberSharing = async (numberKey: string, request: REST_schema.WhatsappNumberSharingUpdateRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/numbers/${encodeURIComponent(numberKey)}/sharing`,
      "POST",
      request
    )) as REST_schema.WhatsappNumberSharingUpdateResponse;

  const whatsappSendTemplate = async (
    conversationId: string,
    request: { templateName: string; language: string; params?: string[] | Record<string, string> }
  ) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/conversations/${encodeURIComponent(conversationId)}/send-template`,
      "POST",
      request
    );

  // --- Official WhatsApp: templates / groups / campaigns management ---
  // `numberKey` picks WHICH number's templates (any number the caller is a member of); omitted, the
  // server falls back to the caller's own connected number.
  const whatsappGetTemplates = async (sync?: boolean, numberKey?: string) => {
    const params = new URLSearchParams();
    if (sync) {
      params.set("sync", "1");
    }
    if (numberKey) {
      params.set("numberKey", numberKey);
    }
    const query = params.toString();
    return await sendWebotRequest(`${baseUrl}/whatsapp/templates${query ? `?${query}` : ""}`, "GET");
  };
  const whatsappValidateTemplate = async (request: unknown) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/templates/validate`, "POST", request);
  const whatsappCreateTemplate = async (request: unknown) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/templates`, "POST", request);
  /**
   * Uploads ONE template header image. The image rides as base64 in the JSON body — the whole API
   * is JSON and the server's body limit is 20mb, so 5MB of image (~6.8MB encoded) fits.
   *
   * The response carries BOTH artefacts a media template needs (Meta's approval handle and a
   * public URL of the same bytes). They are not interchangeable, so the caller passes both
   * straight into the create request rather than picking one.
   */
  const whatsappUploadTemplateMedia = async (request: {
    dataBase64: string;
    fileName: string;
    mimeType: string;
  }) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/templates/media`,
      "POST",
      request
    )) as REST_schema.WhatsappTemplateMediaResponse;

  const whatsappCreateStarterPack = async (numberKey?: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/templates/starter-pack`, "POST", numberKey ? { numberKey } : undefined);
  const whatsappTemplateAction = async (
    templateId: string,
    action: "edit" | "unpause" | "sync" | "delete",
    request?: unknown
  ) =>
    await sendWebotRequest(
      `${baseUrl}/whatsapp/templates/${encodeURIComponent(templateId)}/${action}`,
      "POST",
      request
    );

  /**
   * Where each param's value comes from, and whether the header is the picked listing's picture.
   * Webot metadata only: no Meta call, the approval stays. The card's listener carries the result.
   */
  const whatsappTemplateSources = async (templateId: string, request: REST_schema.WhatsappTemplateSourcesRequest) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/templates/${encodeURIComponent(templateId)}/sources`, "POST", request);

  /** Per-template counters across every rail (campaign / inbox / api / automation). */
  const whatsappTemplateStats = async (request: REST_schema.WhatsappTemplateStatsRequest) =>
    (await sendWebotRequest(`${baseUrl}/whatsapp/templates/stats`, "POST", request)) as REST_schema.WhatsappTemplateStatsResponse;

  /** The paged outbound-messages log of one number (campaign / inbox / api rails). */
  const whatsappSentMessagesLog = async (request: REST_schema.WhatsappSentLogRequest) =>
    (await sendWebotRequest(`${baseUrl}/whatsapp/messages/log`, "POST", request)) as REST_schema.WhatsappSentLogResponse;

  const whatsappGetGroups = async () => await sendWebotRequest(`${baseUrl}/whatsapp/groups`, "GET");
  const whatsappCreateGroup = async (request: unknown) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups`, "POST", request);
  const whatsappGetGroup = async (groupId: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups/${encodeURIComponent(groupId)}`, "GET");
  const whatsappUpdateGroup = async (groupId: string, request: { name: string }) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups/${encodeURIComponent(groupId)}/update`, "POST", request);
  const whatsappDeleteGroup = async (groupId: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups/${encodeURIComponent(groupId)}/delete`, "POST");
  const whatsappQueryGroupMembers = async (groupId: string, request: unknown) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups/${encodeURIComponent(groupId)}/members/query`, "POST", request);
  const whatsappAddGroupMembers = async (groupId: string, members: unknown[]) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups/${encodeURIComponent(groupId)}/members/add`, "POST", { members });
  const whatsappRemoveGroupMembers = async (groupId: string, phones: string[]) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/groups/${encodeURIComponent(groupId)}/members/remove`, "POST", { phones });

  // --- Audiences (קהלים) — the contacts area's recipient lists. Same collection as the legacy
  // /whatsapp/groups endpoints, but auth-only (works for crm-only accounts) and org-sharing aware.
  const audiencesList = async () =>
    (await sendWebotRequest(`${baseUrl}/audiences`, "GET")) as REST_schema.WhatsappListGroupsResponse;
  const audiencesCreate = async (request: REST_schema.WhatsappCreateGroupRequest) =>
    (await sendWebotRequest(`${baseUrl}/audiences`, "POST", request)) as REST_schema.WhatsappCreateGroupResponse;
  const audiencesCreateFromSelection = async (request: REST_schema.CreateAudienceFromSelectionRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/audiences/from-selection`,
      "POST",
      request
    )) as REST_schema.CreateAudienceFromSelectionResponse;
  const audiencesGet = async (audienceId: string) =>
    (await sendWebotRequest(`${baseUrl}/audiences/${encodeURIComponent(audienceId)}`, "GET")) as REST_schema.WhatsappGroupView &
      REST_schema.WhatsappGroupMembersQueryResponse;
  const audiencesUpdate = async (audienceId: string, request: { name: string }) =>
    await sendWebotRequest(`${baseUrl}/audiences/${encodeURIComponent(audienceId)}/update`, "POST", request);
  const audiencesDelete = async (audienceId: string) =>
    await sendWebotRequest(`${baseUrl}/audiences/${encodeURIComponent(audienceId)}/delete`, "POST");
  const audiencesMembersQuery = async (audienceId: string, request: REST_schema.WhatsappGroupMembersQueryRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/audiences/${encodeURIComponent(audienceId)}/members/query`,
      "POST",
      request
    )) as REST_schema.WhatsappGroupMembersQueryResponse;
  const audiencesMembersAdd = async (audienceId: string, members: REST_schema.WhatsappGroupMemberInput[]) =>
    (await sendWebotRequest(`${baseUrl}/audiences/${encodeURIComponent(audienceId)}/members/add`, "POST", {
      members
    })) as REST_schema.WhatsappCreateGroupResponse;
  const audiencesMembersRemove = async (audienceId: string, phones: string[]) =>
    (await sendWebotRequest(`${baseUrl}/audiences/${encodeURIComponent(audienceId)}/members/remove`, "POST", {
      phones
    })) as { memberCount: number };

  // --- Blocked numbers (blacklist). NOT gated on officialWhatsapp server-side — the list also
  // governs the extension (WhatsApp Web) send loops. `whatsappBlacklistCheck` is the FAIL-CLOSED
  // bulk gate: callers must treat ANY throw as "cannot send" (see utils/whatsapp/blockedGate.ts).
  const whatsappBlacklistQuery = async (request: REST_schema.WhatsappBlacklistQueryRequest = {}) =>
    (await sendWebotRequest(`${baseUrl}/whatsapp/blacklist/query`, "POST", request)) as REST_schema.WhatsappBlacklistQueryResponse;
  const whatsappBlacklistAdd = async (request: REST_schema.WhatsappBlacklistAddRequest) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/blacklist`, "POST", request);
  const whatsappBlacklistRemove = async (request: REST_schema.WhatsappBlacklistRemoveRequest) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/blacklist/remove`, "POST", request);
  const whatsappBlacklistCheck = async (request: REST_schema.WhatsappBlacklistCheckRequest) =>
    (await sendWebotRequest(`${baseUrl}/whatsapp/blacklist/check`, "POST", request)) as REST_schema.WhatsappBlacklistCheckResponse;

  const whatsappCreateCampaign = async (request: REST_schema.WhatsappCreateCampaignRequest) =>
    (await sendWebotRequest(`${baseUrl}/whatsapp/campaigns`, "POST", request)) as REST_schema.WhatsappCreateCampaignResponse;
  const whatsappListingShareStatus = async (request: REST_schema.WhatsappListingShareStatusRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/listing-share/status`,
      "POST",
      request
    )) as REST_schema.WhatsappListingShareStatusResponse;
  const whatsappListingShareSend = async (request: REST_schema.WhatsappListingShareSendRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/listing-share/send`,
      "POST",
      request
    )) as REST_schema.WhatsappListingShareSendResponse;
  const whatsappQueryCampaigns = async (request: unknown) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/campaigns/query`, "POST", request);
  const whatsappGetCampaign = async (campaignId: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}`, "GET");
  const whatsappCampaignPreview = async (campaignId: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/preview`, "POST");
  // A reply to one responder from the campaign page. It is the inbox's own free-form send
  // underneath (24h window, recording, handoff), so a closed window comes back as the same
  // WINDOW_CLOSED the messages page shows; the answer carries the recipient's fresh row.
  const whatsappCampaignReply = async (campaignId: string, waId: string, request: REST_schema.WhatsappCampaignReplyRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/responses/${encodeURIComponent(waId)}/reply`,
      "POST",
      request
    )) as REST_schema.WhatsappCampaignReplyResponse;
  // The send pace, editable after create. The runner reads it on every slice, so a change made
  // mid-run takes effect at the next message.
  const whatsappCampaignSetPacing = async (campaignId: string, request: REST_schema.WhatsappCampaignPacingRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/pacing`,
      "POST",
      request
    )) as REST_schema.WhatsappCampaignPacingResponse;
  // Delivery + response, one row per person; the responders come first under the activity sort.
  const whatsappCampaignRecipients = async (campaignId: string, request: REST_schema.WhatsappCampaignRecipientsQueryRequest) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/recipients/query`,
      "POST",
      request
    )) as REST_schema.WhatsappCampaignRecipientsQueryResponse;
  const whatsappCampaignAction = async (campaignId: string, action: "start" | "pause" | "resume" | "cancel") =>
    await sendWebotRequest(`${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/${action}`, "POST");
  // "שליחה בכל זאת": one person the campaign's recency window skipped. The server re-queues them
  // with the window lifted and the RUNNER sends them (every other gate still applies).
  const whatsappCampaignSendAnyway = async (campaignId: string, waId: string) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/recipients/${encodeURIComponent(waId)}/send-anyway`,
      "POST"
    )) as REST_schema.WhatsappSendAnywayResponse;
  const whatsappCampaignRetryFailed = async (campaignId: string, contactIds?: string[]) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/retry-failed`, "POST", { contactIds });
  /** Default values for params that resolve empty per recipient — set from the preview gate. */
  const whatsappCampaignSetParamFallbacks = async (campaignId: string, paramFallbacks: Record<string, string>) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/campaigns/${encodeURIComponent(campaignId)}/param-fallbacks`, "POST", { paramFallbacks });

  // --- Scheduled single sends (one_off campaigns riding the campaign engine) ---
  const whatsappScheduledSendCreate = async (request: REST_schema.WhatsappCreateScheduledSendRequest) =>
    (await sendWebotRequest(`${baseUrl}/whatsapp/scheduled-sends`, "POST", request)) as REST_schema.WhatsappCreateScheduledSendResponse;
  const whatsappScheduledSendsQuery = async (request: REST_schema.WhatsappScheduledSendsQueryRequest = {}) =>
    (await sendWebotRequest(
      `${baseUrl}/whatsapp/scheduled-sends/query`,
      "POST",
      request
    )) as REST_schema.WhatsappScheduledSendsQueryResponse;
  const whatsappScheduledSendCancel = async (campaignId: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/scheduled-sends/${encodeURIComponent(campaignId)}/cancel`, "POST");
  const whatsappScheduledSendReschedule = async (campaignId: string, scheduledFor: string) =>
    await sendWebotRequest(`${baseUrl}/whatsapp/scheduled-sends/${encodeURIComponent(campaignId)}/reschedule`, "POST", {
      scheduledFor
    });

  const getFacebookSources = async (): Promise<GetFacebookSourcesResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/sources`, "GET")) as GetFacebookSourcesResponse;
  };

  const checkFacebookSource = async (request: CheckFacebookSourceRequest): Promise<CheckFacebookSourceResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/sources/check`, "POST", request)) as CheckFacebookSourceResponse;
  };

  const addFacebookSource = async (request: AddFacebookSourceRequest): Promise<AddFacebookSourceResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/sources`, "POST", request)) as AddFacebookSourceResponse;
  };

  const importFacebookSources = async (request: ImportFacebookSourcesRequest): Promise<ImportFacebookSourcesResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/sources/import`, "POST", request)) as ImportFacebookSourcesResponse;
  };

  const updateFacebookSource = async (id: string, patch: UpdateFacebookSourceRequest): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/sources/update`, "POST", { id, ...patch });
  };

  const removeFacebookSource = async (id: string): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/sources/remove`, "POST", { id });
  };

  const getFacebookLeads = async (request: GetFacebookLeadsRequest): Promise<GetFacebookLeadsResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/leads/query`, "POST", request)) as GetFacebookLeadsResponse;
  };

  const getFacebookLeadDetail = async (leadId: string): Promise<GetFacebookLeadDetailResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/leads/${encodeURIComponent(leadId)}`, "GET")) as GetFacebookLeadDetailResponse;
  };

  const setFacebookLeadStatus = async (leadId: string, status: FacebookLeadStatus): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/leads/${encodeURIComponent(leadId)}/status`, "POST", { status });
  };

  const convertFacebookLead = async (leadId: string, request: ConvertFacebookLeadRequest): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/leads/${encodeURIComponent(leadId)}/convert`, "POST", request);
  };

  const getFacebookLeadNotes = async (leadId: string): Promise<GetFacebookLeadNotesResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/leads/${encodeURIComponent(leadId)}/notes`, "GET")) as GetFacebookLeadNotesResponse;
  };

  const addFacebookLeadNote = async (leadId: string, note: string, organizationIds?: string[]): Promise<{ note: UserNote }> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/leads/${encodeURIComponent(leadId)}/notes`, "POST", {
      note,
      ...(organizationIds?.length ? { organizationIds } : {})
    })) as { note: UserNote };
  };

  const getFacebookPersonHistory = async (request: GetFacebookPersonHistoryRequest): Promise<GetFacebookPersonHistoryResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/people/history`, "POST", request)) as GetFacebookPersonHistoryResponse;
  };

  // The on-demand refresh ("רענון לידים"). Both answer HTTP 200 for every business outcome — a
  // refusal (already used today, already running, nothing due) comes back as a normal body, so a
  // THROW from either means transport or a server without the endpoint (404), never "not allowed".
  const getFacebookRefreshStatus = async (): Promise<FacebookRefreshStatus> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/refresh/status`, "GET")) as FacebookRefreshStatus;
  };

  const startFacebookRefresh = async (): Promise<StartFacebookRefreshResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/refresh`, "POST", {})) as StartFacebookRefreshResponse;
  };

  // --- Facebook sources admin (webot-admin only; gated server-side by validateWebotAdmin) ---

  const getFacebookAdminSourcesStats = async (): Promise<GetFacebookAdminSourcesStatsResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/admin/sources-stats`, "GET")) as GetFacebookAdminSourcesStatsResponse;
  };

  const getFacebookAdminSourceDetail = async (id: string): Promise<FacebookAdminSourceDetailResponse> => {
    return (await sendWebotRequest(`${baseUrl}/facebook/admin/sources/${encodeURIComponent(id)}/detail`, "GET")) as FacebookAdminSourceDetailResponse;
  };

  const setFacebookSourceActive = async (id: string, active: boolean): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/admin/sources/${encodeURIComponent(id)}/active`, "POST", { active });
  };

  const rescrapeFacebookSource = async (id: string): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/admin/sources/${encodeURIComponent(id)}/rescrape`, "POST");
  };

  const adminRemoveFacebookSource = async (id: string): Promise<void> => {
    await sendWebotRequest(`${baseUrl}/facebook/admin/sources/${encodeURIComponent(id)}/remove`, "POST");
  };

  return (
    <WebotServerContext.Provider
      value={{
        getOrgAdminStats,
        markNoSpamContacts,
        contactsList,
        contactsLeadsBoard,
        contactsHolders,
        contactsLookup,
        contactsListingSellers,
        contactsPresence,
        contactsBook,
        contactsHoldersAdd,
        contactsHoldersRemove,
        contactsSummary,
        contactsTags,
        contactsAssignments,
        crmAssignContactHandler,
        crmAssignContactHandlersBulk,
        contactsMatchesCounts,
        contactsListingLinks,
        contactsOfficeMinisites,
        contactsGet,
        contactsGetMany,
        contactsUpdate,
        contactsCreate,
        contactsBulkCreate,
        contactsChangePhone,
        contactsRecordBulkMessage,
        contactsDelete,
        contactsBulkUpdate,
        contactsExportCsv,
        crmOrgOverview,
        crmOrgUpdateSettings,
        crmOrgUpdateMember,
        crmOrgUpdateMembersBulk,
        crmOrgUpdateMemberRole,
        crmOrgPool,
        crmOrgPoolPreview,
        crmOrgPoolMigrate,
        crmOrgPoolMembersSync,
        crmOrgPoolProgress,
        crmOrgPoolReverse,
        orgDirectory,
        getBuyersSuggestionsForListingAndRealtor,
        getLastBuyersSuggestionsForListing,
        getListingCardPage,
        webotCalendarOnEventCreated,
        calendarTaskAssign,
        calendarTaskUpdate,
        calendarTaskCancel,
        webotCalendarOnEventUpdated,
        webotCalendarOnEventDeleted,
        getCalendarClientNoteStatus,
        googleCalendarGetStatus,
        googleCalendarStartConnect,
        googleCalendarDisconnect,
        googleCalendarSyncNow,
        googleCalendarListCalendars,
        googleCalendarUpdateSettings,
        getWebotRealtorInfo,
        publishListingToMadlan,
        madlanAutocomplete,
        contactSearch,
        getContactDetails,
        getContactSmartDefaults,
        getUpdateContactWebhookForCustomer,
        rotateUpdateContactWebhookKey,
        generateCreative,
        generateMakeovers,
        setMakeoverShowcase,
        saveListingPresentation,
        getListingPresentation,
        getWebotListingMakeovers,
        deleteMakeover,
        removeListingWatermarks,
        getMcpConnectionInfo,
        getAgencyOffer,
        registerAgencyOfferInterest,
        getMyCoinBalance,
        adminSearchUsers,
        adminSearchOrgs,
        adminGetUserProfile,
        adminGetUserStats,
        adminUpdateUser,
        dbExplorerInfo,
        dbExplorerListTables,
        dbExplorerTableSchema,
        dbExplorerBrowseRows,
        dbExplorerInsertRow,
        dbExplorerUpdateRow,
        dbExplorerDeleteRow,
        dbExplorerRunSql,
        salesMapGetSnapshot,
        salesMapGhlContext,
        salesMapGhlLinks,
        salesMapGhlLink,
        salesMapGhlUnlink,
        salesMapGhlLookup,
        salesMapGhlContact,
        salesMapGhlAddNote,
        salesMapGhlCreateContact,
        salesMapGhlSetStage,
        salesMapGhlAssign,
        salesMapGhlTags,
        salesTrainingGetManifest,
        salesTrainingGetPage,
        salesTrainingGetAudio,
        salesTrainingV2GetAccess,
        salesTrainingV2GetPage,
        salesTrainingV2ListViewers,
        salesTrainingV2AddViewer,
        salesTrainingV2RemoveViewer,
        salesRecruitingList,
        salesRecruitingSetReview,
        salesRecruitingAddNote,
        salesRecruitingDeleteNote,
        salesRecruitingAction,
        salesRecruitingSetAutomation,
        salesRecruitingRunIntake,
        adminLocationsOverview,
        adminLocationsListCities,
        adminLocationsCityDetail,
        adminLocationsGetLocation,
        adminLocationsSearch,
        locationsAutocomplete,
        locationsStreetNeighborhood,
        adminLocationsResolvePreview,
        adminLocationsIssues,
        adminLocationsLookup,
        adminLocationsSourceMappings,
        adminLocationsSampleListings,
        adminLocationsListOverrides,
        adminLocationsCreateOverride,
        adminLocationsDeleteOverride,
        adminLocationsUnresolved,
        adminLocationsUnresolvedDetail,
        adminLocationsPropose,
        adminLocationsListProposals,
        adminLocationsApplyProposal,
        adminLocationsDismissProposal,
        adminLocationsProvenance,
        adminLocationsProvenanceListings,
        adminGetUserCoins,
        adminGetOrgCoins,
        adminGrantCoinPack,
        adminAdjustCoinPack,
        whatsappGetStatus,
        whatsappBeginConnect,
        whatsappCompleteConnect,
        whatsappReportConnectDiagnostics,
        whatsappDisconnect,
        whatsappCheckStatus,
        whatsappGetConversations,
        whatsappGetMessages,
        whatsappMarkRead,
        whatsappSendMessage,
        whatsappListNumberTemplates,
        whatsappTestAssistant,
        whatsappUpdateAssistant,
        whatsappGetNumberSharing,
        whatsappUpdateNumberSharing,
        whatsappSendTemplate,
        whatsappGetTemplates,
        whatsappUploadTemplateMedia,
        whatsappTemplateStats,
        whatsappSentMessagesLog,
        whatsappValidateTemplate,
        whatsappCreateTemplate,
        whatsappCreateStarterPack,
        whatsappTemplateAction,
        whatsappTemplateSources,
        whatsappGetGroups,
        whatsappCreateGroup,
        whatsappGetGroup,
        whatsappUpdateGroup,
        whatsappDeleteGroup,
        whatsappQueryGroupMembers,
        whatsappAddGroupMembers,
        whatsappRemoveGroupMembers,
        audiencesList,
        audiencesCreate,
        audiencesCreateFromSelection,
        audiencesGet,
        audiencesUpdate,
        audiencesDelete,
        audiencesMembersQuery,
        audiencesMembersAdd,
        audiencesMembersRemove,
        whatsappBlacklistQuery,
        whatsappBlacklistAdd,
        whatsappBlacklistRemove,
        whatsappBlacklistCheck,
        whatsappCreateCampaign,
        whatsappListingShareStatus,
        whatsappListingShareSend,
        whatsappQueryCampaigns,
        whatsappGetCampaign,
        whatsappCampaignPreview,
        whatsappCampaignSetParamFallbacks,
        whatsappCampaignRecipients,
        whatsappCampaignReply,
        whatsappCampaignSetPacing,
        whatsappCampaignAction,
        whatsappCampaignRetryFailed,
        whatsappCampaignSendAnyway,
        whatsappScheduledSendCreate,
        whatsappScheduledSendsQuery,
        whatsappScheduledSendCancel,
        whatsappScheduledSendReschedule,
        getFacebookSources,
        checkFacebookSource,
        addFacebookSource,
        importFacebookSources,
        updateFacebookSource,
        removeFacebookSource,
        getFacebookLeads,
        getFacebookLeadDetail,
        setFacebookLeadStatus,
        convertFacebookLead,
        getFacebookLeadNotes,
        addFacebookLeadNote,
        getFacebookPersonHistory,
        getFacebookRefreshStatus,
        startFacebookRefresh,
        getFacebookAdminSourcesStats,
        getFacebookAdminSourceDetail,
        setFacebookSourceActive,
        rescrapeFacebookSource,
        adminRemoveFacebookSource,
        getListingOrgActivity,
        claimMarketListing,
        linkListingSource,
        unlinkListingSource,
        linkListingContact,
        getListingSourceCandidates
      }}
    >
      {children}
    </WebotServerContext.Provider>
  );
}

export { WebotServerProvider, WebotServerContext };
